// Rich content per care type used on the Results page. Each entry contains
// the headline copy, a "what it is" / "what's included" / "who it's best for"
// breakdown, a 2026 monthly cost band, comparison context, and a FAQ block
// (which also feeds FAQPage JSON-LD on the page).

export const CARE_TYPES = {
  "Independent Living": {
    slug: "independent-living",
    icon: "🌿",
    tagline: "An active, maintenance-free lifestyle for healthy seniors",
    longDescription:
      "Independent Living communities are designed for active seniors who want to ditch the burden of homeownership — cooking, cleaning, yard work, repairs — while gaining a vibrant social community, beautiful amenities, and the peace of mind that comes with knowing help is nearby if it's ever needed. Your loved one keeps a private apartment, comes and goes as they please, and gets to focus on what they actually enjoy.",
    includes: [
      "Private apartment with kitchenette",
      "Restaurant-style dining (1–3 meals/day)",
      "Housekeeping & maintenance",
      "Scheduled transportation",
      "Fitness center, pools, walking paths",
      "Social activities, classes, outings",
      "24/7 emergency call system",
    ],
    bestFor: [
      "Seniors 62+ who are healthy and self-sufficient",
      "Anyone tired of maintaining a large home",
      "People who feel isolated or want more social life",
      "Spouses looking for a smaller, easier next chapter",
    ],
    costLow: 3500,
    costHigh: 5500,
    differsFrom:
      "Unlike Assisted Living, Independent Living does NOT include help with bathing, dressing, or medication. If those needs emerge later, residents can typically transition to assisted living within the same community (a setup called a Continuing Care Retirement Community, or CCRC).",
    faq: [
      {
        q: "Will I lose my independence?",
        a: "Quite the opposite. Residents come and go freely, drive their own cars, host family for dinner, and travel. The difference: there's no lawn to mow, no leaky faucet to fix. You get more independence — just over your time and energy, not over your daily routine.",
      },
      {
        q: "What if my health changes after I move in?",
        a: "Most communities offer 'aging in place' — when you need help with bathing, dressing, or medications, you stay in your same apartment and add à-la-carte care, OR transition to the assisted living wing of the same campus. Your specialist will help you pick a community with the right continuum.",
      },
      {
        q: "Are these places only for wealthy seniors?",
        a: "No. Independent Living typically runs $3,500–$5,500/month — competitive with the all-in cost of owning a home (mortgage + utilities + maintenance + groceries + healthcare). Many residents downsize from a paid-off home and the math works out favorably.",
      },
      {
        q: "Can my pet come with me?",
        a: "Most Independent Living communities welcome cats and small dogs, often with a one-time fee. Some have on-site dog parks and pet washing stations. Your specialist will filter for pet-friendly options.",
      },
    ],
  },
  "Assisted Living": {
    slug: "assisted-living",
    icon: "🤝",
    tagline: "Compassionate help with daily tasks, dignity always intact",
    longDescription:
      "Assisted Living is a warm, home-like environment for seniors who need help with one or more daily tasks — bathing, dressing, medications, mobility, meals — but who don't have dementia or a serious medical condition requiring nursing care. Residents have private apartments, three chef-prepared meals a day, and trained staff available 24/7 to step in when needed. The goal: maximize independence and dignity while removing daily struggle.",
    includes: [
      "Private apartment or studio",
      "3 meals/day + snacks",
      "Help with bathing, dressing, grooming",
      "Medication management",
      "24/7 trained staff & emergency response",
      "Housekeeping & laundry",
      "Transportation to medical appointments",
      "Social activities, exercise classes, outings",
    ],
    bestFor: [
      "Seniors needing help with 2+ daily tasks",
      "Anyone struggling with medications, falls, or isolation",
      "Families worried about safety but not yet needing nursing care",
      "Seniors after a hospital stay or major life event",
    ],
    costLow: 4500,
    costHigh: 6500,
    differsFrom:
      "Memory Care is similar but secured (residents can't exit unsupervised) and built specifically for dementia. Nursing Homes are for residents needing 24/7 skilled medical care — typically following a serious illness. Assisted Living is the middle ground: real help, but in a residential, dignified setting.",
    faq: [
      {
        q: "How much help is 'help'?",
        a: "Every resident gets a personalized care plan. You only pay for the level you need — typically tiered (low / medium / high care). Some residents need just medication reminders; others need help getting dressed every morning. The plan adjusts as needs change.",
      },
      {
        q: "What if mom doesn't want to give up her stuff?",
        a: "She doesn't have to. Most apartments fit a bedroom set, favorite chair, photos, artwork, and small kitchen items. We've seen residents bring beloved couches, pianos, even gardening containers for their patio. The goal is for the apartment to feel like home, not a hotel room.",
      },
      {
        q: "Are staff trained, or are they just 'helpers'?",
        a: "All caregivers complete state-required certification (typically 40–80 hours of training plus ongoing CEUs). Med techs have additional certification. Many communities also employ on-staff RNs. Always ask about staff turnover during your tour — it's the #1 quality indicator.",
      },
      {
        q: "Can Medicare or Medicaid pay for this?",
        a: "Medicare does NOT cover the room and board of assisted living. Medicaid does, in many states, via specific waivers — but waitlists are long. Most families pay privately using a mix of savings, long-term care insurance, VA Aid & Attendance benefits (for vets), and home sale proceeds. Your specialist can build a funding plan with you.",
      },
      {
        q: "How quickly can someone move in?",
        a: "If a unit is available: 1–2 weeks. If there's a waitlist: 30–90 days for popular communities. In urgent situations (recent fall, hospital discharge), many communities can fast-track for medical urgency.",
      },
    ],
  },
  "Memory Care": {
    slug: "memory-care",
    icon: "🧠",
    tagline: "Secure, specialized care designed around dementia",
    longDescription:
      "Memory Care is a specialized, secured environment for seniors living with Alzheimer's, dementia, or related conditions. Every detail — from the layout (looped hallways prevent dead-ends and frustration), to staff training (de-escalation, validation therapy), to daily routines (familiar, structured), to safety (secured exits, wander prevention) — is built around the realities of cognitive change. The result: less confusion, less agitation, more peaceful days, and the kind of safety that lets you sleep at night.",
    includes: [
      "Secured apartment or shared suite",
      "24/7 supervision by dementia-trained staff",
      "All meals, snacks, hydration prompts",
      "Help with all daily tasks (bathing, dressing, toileting)",
      "Medication management & health monitoring",
      "Structured daily routine + therapeutic activities",
      "Wander prevention & gentle re-direction",
      "Family support, education, and grief resources",
    ],
    bestFor: [
      "Seniors with a dementia or Alzheimer's diagnosis",
      "Anyone wandering, getting lost, or unsafe alone",
      "Families exhausted from full-time caregiving",
      "Seniors with sundowning, agitation, or aggression",
    ],
    costLow: 6500,
    costHigh: 9000,
    differsFrom:
      "Assisted Living looks similar but is NOT secured — residents can leave at any time, which is dangerous for someone with cognitive impairment. Nursing Homes provide more medical care but less dementia-specific programming. Memory Care is purpose-built for cognitive change, with environmental and staffing choices that just don't exist elsewhere.",
    faq: [
      {
        q: "Will mom feel locked up?",
        a: "Quality memory care doesn't feel locked-down — it feels safe. Common spaces flow gently, courtyards are secured but accessible, and residents often have more freedom than at home (where they may have been confined to one floor for safety). Staff are trained to validate, not correct, which dramatically reduces agitation.",
      },
      {
        q: "What if she doesn't want to go?",
        a: "This is the hardest part. Most families experience some resistance. Your specialist can connect you with social workers who specialize in dementia transitions — they have proven scripts and approaches. Often we recommend framing it as 'a trial stay' or 'while we renovate the house.' Within 2–4 weeks, most residents settle in and family report less anxiety on both sides.",
      },
      {
        q: "How do staff handle agitation, aggression, or sundowning?",
        a: "Dementia-trained staff use validation therapy (joining their reality instead of correcting), redirection, structured activity, and environmental cues (calmer lighting in the afternoon to ease sundowning). Medication is a last resort. Always ask what % of residents are on antipsychotics during your tour — lower is better.",
      },
      {
        q: "What's the cost difference vs. assisted living?",
        a: "Memory Care typically runs $1,500–$3,000/month more than Assisted Living due to higher staff ratios (1:5 vs 1:8 typical), specialized training, and secured environments. The investment is significant — but the alternative (24/7 family caregiving or unsafe living) usually costs more in caregiver health, lost income, and emergency care.",
      },
      {
        q: "Can my mom move from assisted living into memory care later?",
        a: "Yes — and many families do exactly this. Communities with both AL and MC under one roof make the transition smoother. Your specialist can prioritize 'campus' communities that offer the continuum so you don't have to move again.",
      },
      {
        q: "How do I know it's actually time for memory care, not assisted living?",
        a: "Triggers: wandering, getting lost in familiar places, leaving the stove on, dressing inappropriately for weather, aggression toward caregivers, mistaking strangers for family, sundowning. If safety is becoming a daily worry, memory care will likely reduce it within the first week.",
      },
    ],
  },
  "Other": {
    slug: "other",
    icon: "💡",
    tagline: "Your situation is unique — let's talk it through",
    longDescription:
      "Based on your answers, you don't fit neatly into one category — and that's actually common. Maybe your loved one is between care levels, has a complex medical situation, or has unique preferences (a faith-based community, a particular geography, a specific budget). The best next step is a conversation with a Placement Director who can hear the full picture and build a custom shortlist of communities that fit.",
    includes: [
      "Free 30-minute discovery call",
      "Personalized care-level recommendation",
      "Curated shortlist of 2–3 vetted communities",
      "Budget and funding-source planning",
      "Tour coordination",
      "Ongoing support through move-in",
    ],
    bestFor: [
      "Complex care needs spanning multiple levels",
      "Tight or unusual budget requirements",
      "Faith, cultural, or geographic preferences",
      "Time-sensitive (post-hospital, urgent) situations",
    ],
    costLow: 3000,
    costHigh: 8000,
    differsFrom: "",
    faq: [
      {
        q: "What does the Placement Director do, exactly?",
        a: "They're licensed senior-living advisors with deep knowledge of the communities in your local market. They hear your situation, recommend a level of care, then build a curated shortlist (typically 2–3 communities) that match your needs, budget, and preferences — saving you weeks of cold-calling and tour scheduling. The service is free to families; communities pay them only after a successful move-in.",
      },
      {
        q: "Are Placement Directors biased toward who pays them most?",
        a: "Reputable ones (including all CareHomeConnect partners) work with a wide network and prioritize fit over fee. We vet partners for ethics, network breadth, and family-first approach — and we drop anyone who pressures families. You can always ask what other communities they considered and why.",
      },
      {
        q: "How fast will I hear back?",
        a: "Most families get a call or text within 24 hours — often within an hour during business hours. If you have an urgent need (recent hospitalization, fall, sudden caregiver loss), tell us in your zip code form and we'll prioritize.",
      },
    ],
  },
};

export function getCareData(careType) {
  return CARE_TYPES[careType] || CARE_TYPES["Other"];
}

export function formatMoney(n) {
  return `$${Math.round(n).toLocaleString()}`;
}
